import React, { useState } from 'react'
import { Dispatch } from 'redux'
import axios from 'axios'
import LoaderButton from '../../../components/loaderButton'
import DobModal from './DobModal'
import ContactInfoModal from './ContactInfoModal'
import { displayNotification } from '../../../actions/notificationsActions'
import { iso8601DateFormat } from '../../../helpers/timeHelper'
import { User } from '../../../core/types'
import { fetchUser, updateUser } from '../userActions'
import { filterContactFields } from '../../../utils/validationHelpers'

interface InsuranceCaptureBtnProps {
  productPackage: any
  user: User
  dispatch: Dispatch<any>
}

const InsuranceCaptureBtn: React.FC<InsuranceCaptureBtnProps> = ({
  productPackage,
  user,
  dispatch,
}) => {
  const [showDobModal, setShowDobModal] = useState(false)
  const [showPhoneModal, setShowPhoneModal] = useState(false)
  const [phoneModalError, setPhoneModalError] = useState<string | null>(null)
  const [phoneModalLoading, setPhoneModalLoading] = useState(false)
  const [dobModalError, setDobModalError] = useState<string | null>(null)
  const [dobModalLoading, setDobModalLoading] = useState(false)

  const verify = (productPackage as any)?.insuranceVerifyDetails
  const missingFields: string[] = []
  const hasExistingInsuranceVerify = verify && Object.keys(verify).length > 0

  if (!productPackage?.vehicle) missingFields.push('Vehicle')
  if (!user?.firstName) missingFields.push('First Name')
  if (!user?.lastName) missingFields.push('Last Name')
  if (!user?.phone) missingFields.push('Phone')
  if (!user?.email) missingFields.push('Email')
  if (!productPackage?.id) missingFields.push('Package')

  const otherMissingFields = filterContactFields(missingFields)
  const isEnabled = otherMissingFields.length === 0 && !hasExistingInsuranceVerify

  const handleConfirmDob = (selectedDob: Date) => {
    const formattedDob = iso8601DateFormat(selectedDob)
    setDobModalLoading(true)
    setDobModalError(null)

    return axios
      .post(
        `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/admin/insurances/verify_link`,
        {
          product_package_id: productPackage.id,
          dob: formattedDob,
        }
      )
      .then((response) => {
        const { message } = response.data
        setDobModalLoading(false)
        dispatch(displayNotification(message))
        setShowDobModal(false)
        setDobModalError(null)
        dispatch(fetchUser(user.id))
      })
      .catch((error) => {
        setDobModalLoading(false)
        const errorMessage = error?.response?.data?.message || 
                             error?.response?.data?.error || 
                             error?.message || 
                             'Failed to send insurance verify link to the customer'
        setDobModalError(errorMessage)
      })
  }

  const handleConfirmPhone = (contactInfo: { phone: string; email: string }) => {
    setPhoneModalLoading(true)
    setPhoneModalError(null)

    return dispatch<any>(updateUser({ 
      id: user.id, 
      phone: contactInfo.phone,
      email: contactInfo.email 
    }))
      .then(() => {
        setPhoneModalLoading(false)
        dispatch<any>(displayNotification('Contact information updated'))
        setShowPhoneModal(false)
        setPhoneModalError(null)
        setShowDobModal(true)
        dispatch<any>(fetchUser(user.id))
      })
      .catch((error) => {
        setPhoneModalLoading(false)
        const errorMessage = error?.response?.data?.message || 
                             error?.response?.data?.error || 
                             error?.message || 
                             'Failed to update contact information'
        setPhoneModalError(errorMessage)
        throw error
      })
  }

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString)

    const formattedDate = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })

    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })

    return `${formattedDate} - ${formattedTime}`
  }

  return (
    <>
      <ContactInfoModal
        isOpen={showPhoneModal}
        onClose={() => {
          setShowPhoneModal(false)
          setPhoneModalError(null)
        }}
        onConfirm={handleConfirmPhone}
        description="Please update the customer's contact details to send the verify link."
        confirmButtonText="Confirm & Continue"
        phone={user.phone}
        email={user.email}
        showPhone={!user?.phone}
        showEmail={!user?.email}
        isLoading={phoneModalLoading}
        serverError={phoneModalError}
      />

      <DobModal
        isOpen={showDobModal}
        onClose={() => {
          setShowDobModal(false)
          setDobModalError(null)
        }}
        onConfirm={handleConfirmDob}
        title="Enter Date of Birth"
        description="Please confirm the date of birth to send the verify link"
        confirmButtonText="Confirm & Send Link"
        dob={user.birthday}
        isLoading={dobModalLoading}
        serverError={dobModalError}
      />

      <div>
        <LoaderButton
          type="button"
          className="button-spacing button-apply-financing button--full-width"
          onClick={() => {
            if (!user?.phone || !user?.email) {
              setShowPhoneModal(true)
            } else {
              setShowDobModal(true)
            }
          }}
          disabled={!isEnabled}
          style={{
            marginBottom: '8px',
            backgroundColor: !isEnabled ? '#ccc' : undefined,
            color: !isEnabled ? '#666' : undefined,
          }}
        >
          Insurance Capture
        </LoaderButton>

        {!isEnabled && (
          <div style={{ marginBottom: '8px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <a href="#" target="_blank" rel="noopener noreferrer">
                View Insurance Information
              </a>
              <span style={{ marginLeft: '8px', fontSize: '14px' }}>✏️</span>
            </div>
            <div style={{ color: '#666', lineHeight: '1.4' }}>
              <div>Created: {formatDateTime(verify?.created_at)}</div>
              <div>Updated: {formatDateTime(verify?.updated_at)}</div>
              <div>Verification ID: {verify?.verify_id || ""}</div>
              <div>Lead ID : {verify?.lead_id || ""}</div>
              <div style={{ marginBottom: '8px' }}>
                Status: {verify?.status || ""}
              </div>
              <div>
                Current Insurance Company:{' '}
                {verify?.current_insurance_company || ""}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

InsuranceCaptureBtn.displayName = 'Insurance Capture'

export default InsuranceCaptureBtn
